export type ModernizedSurface =
  | "homepage"
  | "checkout"
  | "profile-settings"
  | "dashboard"
  | "course-dashboard-details"
  | "quiz-report"
  | "course-catalog"
  | "admin"
  | "admin-content-production"
  | "customer-account"
  | "account-edge-states";

export type TokenPassStatus = "foundation" | "active-pass" | "staging-complete";

export type TokenPass = {
  readonly surface: ModernizedSurface;
  readonly status: TokenPassStatus;
  readonly criticalMobileChecks: readonly string[];
};

export const stagedTokenSurfaces: readonly ModernizedSurface[] = [
  "homepage",
  "checkout",
  "profile-settings",
  "dashboard",
  "course-dashboard-details",
  "quiz-report",
  "course-catalog",
  "admin",
  "admin-content-production",
  "customer-account",
  "account-edge-states",
];

export const tokenPasses: readonly TokenPass[] = [
  {
    surface: "course-catalog",
    status: "active-pass",
    criticalMobileChecks: [
      "course group cards use token-backed colors without inline card styles",
      "course CTA text wraps inside fixed-width mobile buttons",
      "expanded course grids stay within the mobile viewport",
    ],
  },
  {
    surface: "admin",
    status: "active-pass",
    criticalMobileChecks: [
      "ActiveAdmin title-bar actions wrap below 768px",
      "filters, forms, and wide tables scroll inside their panels",
      "logged-out admin login remains viewport constrained",
    ],
  },
  {
    surface: "customer-account",
    status: "active-pass",
    criticalMobileChecks: [
      "billing and cancellation actions wrap without clipping",
      "failed-payment recovery form fields stack at narrow widths",
      "timer preference controls remain full-width and tappable",
    ],
  },
  {
    surface: "dashboard",
    status: "active-pass",
    criticalMobileChecks: [
      "dashboard header actions wrap without horizontal scroll",
      "score/test-date controls remain tappable",
      "module tiles scroll internally without trapping the page",
    ],
  },
  {
    surface: "course-dashboard-details",
    status: "staging-complete",
    criticalMobileChecks: [
      "course dashboard upsell, module tiles, and locked overlays stay inside the viewport",
      "module progress bars and long module names wrap without resizing tiles",
      "dashboard inner content has no horizontal scroll at 390px",
    ],
  },
  {
    surface: "quiz-report",
    status: "active-pass",
    criticalMobileChecks: [
      "quiz footer clears safe-area insets",
      "image-answer labels remain aligned",
      "report cards and explanations wrap long content",
    ],
  },
  {
    surface: "admin-content-production",
    status: "staging-complete",
    criticalMobileChecks: [
      "Question Sets Needing Video KPIs stack at phone width",
      "content-production filters remain full-width and tappable",
      "wide content-production tables scroll inside their panels",
    ],
  },
  {
    surface: "account-edge-states",
    status: "staging-complete",
    criticalMobileChecks: [
      "failed billing recovery Stripe Elements stack without clipping",
      "renewal, cancellation, and upgrade actions wrap at narrow widths",
      "subscription dates and long course labels wrap inside account panels",
    ],
  },
  {
    surface: "homepage",
    status: "foundation",
    criticalMobileChecks: ["sample video loads after hash refresh"],
  },
  {
    surface: "checkout",
    status: "foundation",
    criticalMobileChecks: ["payment totals and errors fit at 390px"],
  },
  {
    surface: "profile-settings",
    status: "foundation",
    criticalMobileChecks: ["billing and cancellation controls remain visible"],
  },
];

export const frontendModernizationPath = "esbuild-typescript-placeholder" as const;

export function markEsbuildReady(root: HTMLElement = document.documentElement): void {
  root.dataset.esbuildModernization = frontendModernizationPath;
  root.dataset.tokenPassSurfaces = tokenPasses.map((pass) => pass.surface).join(",");
}
